import { TranslationKey } from "@/modules/i18n"
import { ChallengeService } from "@duck/api-definitions"
import { AssignmentTab } from "./WorkspaceTabs/Assignment/Assignment.tab"
import { SolutionTab } from "./WorkspaceTabs/Solution/Solution.tab"
import { SqlTab } from "./WorkspaceTabs/Sql/Sql.tab"
import { NotesTab } from "./WorkspaceTabs/Notes/Notes.tab"
import { SqlCheatSheetTab } from "./WorkspaceTabs/SqlCheatSheet/SqlCheatSheet.tab"
import { TipsHistoryTab } from "./WorkspaceTabs/TipsHistory/TipsHistory.tab"
import {
  BookOpen,
  ClipboardList,
  Database,
  Lightbulb,
  type LucideIcon,
  PenLine,
  StickyNote,
} from "lucide-react"
import { PythonIcon } from "@/components/Workspace/WorkspaceSettings/panels/icons/PythonIcon"

export type WorkspaceTabComponentProps = {
  data?: string
  onUpdateData?: (data: string) => void
  isVisible?: boolean
}
import { FC } from "react"

export type WorkspaceTabsItem = {
  label: TranslationKey
  Component: FC<WorkspaceTabComponentProps>
  Icon: LucideIcon
  /** Tailwind text color class applied to the tab icon (always shown). */
  iconColor: string
  onlyOne?: boolean
  /** If true, this tab's content is rendered as a portal singleton at the WorkspaceTabs level, surviving view remounts. */
  portalSingleton?: boolean
}

const AssignmentTabWrapper: FC<WorkspaceTabComponentProps> = () => <AssignmentTab />

const SolutionTabWrapper: FC<WorkspaceTabComponentProps> = () => <SolutionTab />

const NotesTabWrapper: FC<WorkspaceTabComponentProps> = () => <NotesTab />

const parseSqlTabData = (data?: string): { query: string; schemaOpen: boolean } => {
  if (!data) return { query: '', schemaOpen: false }
  try {
    const parsed = JSON.parse(data)
    if (typeof parsed === 'object' && parsed !== null && 'query' in parsed) {
      return { query: parsed.query ?? '', schemaOpen: !!parsed.schemaOpen }
    }
  } catch {
    // Legacy plain query string
  }
  return { query: data, schemaOpen: false }
}

export const serializeSqlTabData = (query: string, schemaOpen: boolean): string =>
  JSON.stringify({ query, schemaOpen })

const SqlTabWrapper: FC<WorkspaceTabComponentProps> = ({ data, onUpdateData, isVisible }) => {
  const { query, schemaOpen } = parseSqlTabData(data)
  return <SqlTab query={query} initialSchemaOpen={schemaOpen} onUpdateData={onUpdateData} isVisible={isVisible} />
}

const SqlCheatSheetTabWrapper: FC<WorkspaceTabComponentProps> = () => <SqlCheatSheetTab />

const TipsHistoryTabWrapper: FC<WorkspaceTabComponentProps> = () => <TipsHistoryTab />

export const workspaceTabsDefinitions: Record<ChallengeService.WorkspaceTabTypeEnum, WorkspaceTabsItem> = {
  [ChallengeService.WorkspaceTabTypeEnum.Assignment]: {
    label: "common.assignment",
    Component: AssignmentTabWrapper,
    Icon: ClipboardList,
    iconColor: "text-amber-800",
  },
  [ChallengeService.WorkspaceTabTypeEnum.Solution]: {
    label: "common.solution",
    Component: SolutionTabWrapper,
    Icon: PenLine,
    iconColor: "text-emerald-800",
  },
  [ChallengeService.WorkspaceTabTypeEnum.Sql]: {
    label: "common.sql",
    Component: SqlTabWrapper,
    Icon: Database,
    iconColor: "text-blue-800",
  },
  [ChallengeService.WorkspaceTabTypeEnum.Notes]: {
    label: "common.notes",
    Component: NotesTabWrapper,
    Icon: StickyNote,
    iconColor: "text-fuchsia-800",
  },
  [ChallengeService.WorkspaceTabTypeEnum.SqlCheatSheet]: {
    label: "common.sqlCheatSheet",
    Component: SqlCheatSheetTabWrapper,
    Icon: BookOpen,
    iconColor: "text-sky-800",
  },
  [ChallengeService.WorkspaceTabTypeEnum.TipsHistory]: {
    label: "challenges.tipsHistory",
    Component: TipsHistoryTabWrapper,
    Icon: Lightbulb,
    iconColor: "text-orange-800",
  },
  [ChallengeService.WorkspaceTabTypeEnum.Python]: {
    label: 'common.python',
    onlyOne: true,
    portalSingleton: true,
    Component: () => null,
    Icon: PythonIcon as unknown as LucideIcon,
    iconColor: "",
  }
}

export const workspaceTabsOrder: ChallengeService.WorkspaceTabTypeEnum[] = [
  ChallengeService.WorkspaceTabTypeEnum.Assignment,
  ChallengeService.WorkspaceTabTypeEnum.Python,
  ChallengeService.WorkspaceTabTypeEnum.Sql,
  ChallengeService.WorkspaceTabTypeEnum.Solution,
  ChallengeService.WorkspaceTabTypeEnum.Notes,
  ChallengeService.WorkspaceTabTypeEnum.SqlCheatSheet,
  ChallengeService.WorkspaceTabTypeEnum.TipsHistory,
]
